// §9d — a sub-issue's schedule must sit inside its parent's. Blocking (unlike issueDependency's
// advisory Start Date warnings): the same rule is enforced server-side in IssueService, and this
// is the client-side half that bounds the date pickers and disables Save before a doomed request
// is ever sent. Kept pure and in one place so all four editing surfaces (Planner inline cells,
// the Planner's inline sub-task row, IssueDetailDialog, IssueLinkPage) and IssueFormDialog's
// sub-issue create share one rule and one set of messages — matching the API's wording, so a
// server-side rejection reads identically to a client-side one.
//
// The rule applies in both directions, and exactly one of them applies to any given issue
// (nesting is single-level): a sub-issue is bounded by its parent, and a parent is bounded by
// the sub-issues it already has, so narrowing a parent past an existing sub-issue is blocked
// too. Each bound is independent — a parent with only a Start Date still constrains BOTH of the
// sub-issue's dates from below — so a partially-scheduled parent is still a real constraint.
// Issues already outside range predate the rule and are left alone until someone edits them.

import type { IIssue } from '@pulse/shared';

import { formatDdMmYyyy } from '../../../lib/date.util';

/** The 'YYYY-MM-DD' limit itself (for a date input's `min`/`max`) plus the message to show when
 *  a value breaks it — the two always travel together, since the message names whichever issue
 *  produced the limit. */
export interface DateBound {
  value: string;
  message: string;
}

/** Limits for one issue's two date fields. Every field is optional: an issue with no parent and
 *  no sub-issues is unconstrained and gets an empty object. */
export interface IssueDateBounds {
  startMin?: DateBound;
  startMax?: DateBound;
  dueMin?: DateBound;
  dueMax?: DateBound;
}

type IssueDates = Pick<IIssue, 'issueKey' | 'startDate' | 'dueDate'>;

/** Bounds for a sub-issue (existing or about to be created) from its parent's own dates. Both of
 *  the sub-issue's dates get the same window, so `startMax`/`dueMin` are the parent's opposite
 *  bound rather than being left open. */
export function subIssueDateBounds(parent: IssueDates | null | undefined): IssueDateBounds {
  if (!parent) return {};
  const bounds: IssueDateBounds = {};
  if (parent.startDate) {
    const lower = { value: parent.startDate, message: `cannot be before ${parent.issueKey}'s Start Date (${formatDdMmYyyy(parent.startDate)}).` };
    bounds.startMin = lower;
    bounds.dueMin = lower;
  }
  if (parent.dueDate) {
    const upper = { value: parent.dueDate, message: `cannot be after ${parent.issueKey}'s Due Date (${formatDdMmYyyy(parent.dueDate)}).` };
    bounds.startMax = upper;
    bounds.dueMax = upper;
  }
  return bounds;
}

/** Bounds for a parent from the sub-issues it already has: its Start Date can't move past the
 *  earliest sub-issue date, and its Due Date can't move before the latest one. Sub-issues with
 *  no dates at all impose nothing. */
export function parentDateBounds(subIssues: IssueDates[]): IssueDateBounds {
  const bounds: IssueDateBounds = {};
  for (const sub of subIssues) {
    for (const [field, value] of [['Start Date', sub.startDate], ['Due Date', sub.dueDate]] as const) {
      if (!value) continue;
      const message = `would leave sub-issue ${sub.issueKey}'s ${field} (${formatDdMmYyyy(value)}) outside this range. Change the sub-issue's dates first.`;
      if (!bounds.startMax || value < bounds.startMax.value) bounds.startMax = { value, message };
      if (!bounds.dueMin || value > bounds.dueMin.value) bounds.dueMin = { value, message };
    }
  }
  return bounds;
}

/** The bounds that apply to `issue`, resolving its parent or its sub-issues out of the project's
 *  already-fetched issue list. A parent that hasn't loaded yet yields no bounds rather than a
 *  false violation — same "a miss means the data isn't here yet" reasoning as
 *  checkStartDateAgainstDependencies. */
export function issueDateBounds(issue: Pick<IIssue, 'id' | 'parentIssueId'>, allIssues: IIssue[]): IssueDateBounds {
  if (issue.parentIssueId) {
    return subIssueDateBounds(allIssues.find((i) => i.id === issue.parentIssueId));
  }
  return parentDateBounds(allIssues.filter((i) => i.parentIssueId === issue.id));
}

/** The `min`/`max` attributes for one field's native date input, so the picker greys out
 *  unreachable days instead of only rejecting them after the fact. Absent bounds are *omitted*
 *  rather than set to undefined: these get spread into DateField's slotProps.htmlInput, which
 *  carries its own `max: '9999-12-31'` default (its year-padding guard), and an explicit
 *  `max: undefined` in the spread would silently wipe that out. */
export function dateInputBounds(field: 'Start Date' | 'Due Date', bounds: IssueDateBounds): { min?: string; max?: string } {
  const min = field === 'Start Date' ? bounds.startMin : bounds.dueMin;
  const max = field === 'Start Date' ? bounds.startMax : bounds.dueMax;
  return { ...(min && { min: min.value }), ...(max && { max: max.value }) };
}

/** The violation message for a single date field, or null when the value is within bounds (or
 *  empty — clearing a date is always allowed). */
export function dateBoundError(
  field: 'Start Date' | 'Due Date',
  value: string | null | undefined,
  bounds: IssueDateBounds,
): string | null {
  if (!value) return null;
  const min = field === 'Start Date' ? bounds.startMin : bounds.dueMin;
  const max = field === 'Start Date' ? bounds.startMax : bounds.dueMax;
  if (min && value < min.value) return `${field} ${min.message}`;
  if (max && value > max.value) return `${field} ${max.message}`;
  return null;
}

/** The first violation across both of an issue's dates — for gating a Save button or a commit
 *  that writes both at once. */
export function issueDatesBoundError(
  dates: { startDate?: string | null; dueDate?: string | null },
  bounds: IssueDateBounds,
): string | null {
  return dateBoundError('Start Date', dates.startDate, bounds) ?? dateBoundError('Due Date', dates.dueDate, bounds);
}
